import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ChatBubbleLeftRightIcon,
    CheckBadgeIcon,
    ChevronDownIcon,
    NoSymbolIcon,
    PlusIcon,
} from "@heroicons/react/20/solid";
import { Badge } from "@/catalyst/badge";
import {
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "@/catalyst/dropdown";
import { FormEmpty, FormRow, FormSection } from "@/common/FormLayout";
import { Logo } from "@/common/Logo/Logo.tsx";
import {
    criterionFor,
    FactField,
    FACTS,
    questionRules,
    ruleOutFor,
    WEIGHTS,
    withFact,
    withRuleOut,
} from "@/features/IcpFit/icp.ts";
import {
    FactRow,
    RuleOutRow,
} from "@/features/Workspace/Settings/IcpProfile/FactRows.tsx";
import { IcpProfile } from "@/types/generated/IcpProfile.ts";
import {
    AuthError,
    AuthHeading,
    BrandButton,
} from "../auth/components/BrandControls.tsx";
import { auditPath, saveAuditProspect } from "./auditProspect.ts";
import { useAuditProspect } from "./AuditProspectContext.tsx";
import { useLightSurface } from "@/app/darkMode.ts";

/**
 * The optional edit behind an audit link (specs/icp-fit/outbound-audit.md
 * §2): the drafted profile in the workspace editor's own pickers, labels and
 * vocabulary, without an account. "Save and continue" goes back to the form.
 *
 * The questions are shown and not edited here: they are free text, and what
 * a visitor without an account may type into our database is kept to the
 * vocabulary's own values.
 */
export default function AuditEditPage() {
    const navigate = useNavigate();
    const { token, state, update } = useAuditProspect();
    const [edited, setEdited] = useState<IcpProfile>();
    const [openRuleOuts, setOpenRuleOuts] = useState<FactField[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();

    if (token === null || state.status !== "ready") {
        return (
            <Page>
                {state.status === "loading" && token !== null ? (
                    <p className="text-sm text-brand-grey-warm">One moment…</p>
                ) : (
                    <AuthHeading
                        title={
                            state.status === "failed"
                                ? "We could not open this page"
                                : "This link has expired"
                        }
                        subtitle={
                            state.status === "failed"
                                ? state.message
                                : "The profile behind it is no longer available."
                        }
                    />
                )}
            </Page>
        );
    }

    const { prospect } = state;
    const draft = edited ?? prospect.rules;
    const questions = questionRules(draft);
    const ruleOutRows = FACTS.filter(
        (fact) =>
            fact.canRuleOut &&
            (ruleOutFor(draft, fact.field) !== undefined ||
                openRuleOuts.includes(fact.field)),
    );
    const ruleOutChoices = FACTS.filter(
        (fact) => fact.canRuleOut && !ruleOutRows.includes(fact),
    );
    const isEmpty = draft.criteria.length === 0;

    const save = async () => {
        setSaving(true);
        setError(undefined);
        try {
            if (edited) {
                update(await saveAuditProspect(token, edited));
            }
            navigate(auditPath(token));
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Something went wrong. Please try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Page>
            <AuthHeading
                title="Is this who you sell to?"
                subtitle={`We read ${prospect.domain} and wrote down the companies you seem to be after. Change anything that is off.`}
            />

            {prospect.description !== "" && (
                <p className="max-w-prose text-base text-brand-grey-warm">
                    {prospect.description}
                </p>
            )}

            <div className="flex flex-col gap-6 text-default">
                <FormSection
                    title="Who you sell to"
                    icon={CheckBadgeIcon}
                    description="Your ideal customer in a few facts. Give the ones that matter most a high weight."
                >
                    {FACTS.map((fact, index) => {
                        const criterion = criterionFor(draft, fact.field);
                        return (
                            <FactRow
                                key={fact.field}
                                fact={fact}
                                values={criterion?.values ?? []}
                                points={criterion?.points ?? WEIGHTS.medium}
                                vocabulary={prospect.vocabulary}
                                // Nothing here depends on a plan yet.
                                hasLens
                                disabled={saving}
                                lockedReason={null}
                                captioned={index === 0}
                                onChange={(values, points) =>
                                    setEdited(
                                        withFact(
                                            draft,
                                            fact.field,
                                            values,
                                            points,
                                        ),
                                    )
                                }
                            />
                        );
                    })}
                </FormSection>

                <FormSection
                    title="Never a fit"
                    icon={NoSymbolIcon}
                    description="Companies with one of these are ruled out, whatever else matches."
                    action={
                        ruleOutChoices.length > 0 && (
                            <Dropdown>
                                <DropdownButton outline>
                                    <PlusIcon />
                                    Add rule-out
                                    <ChevronDownIcon />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                    {ruleOutChoices.map((fact) => (
                                        <DropdownItem
                                            key={fact.field}
                                            onClick={() =>
                                                setOpenRuleOuts([
                                                    ...openRuleOuts,
                                                    fact.field,
                                                ])
                                            }
                                        >
                                            <fact.icon />
                                            <DropdownLabel>
                                                {fact.label}
                                            </DropdownLabel>
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        )
                    }
                >
                    {ruleOutRows.length === 0 ? (
                        <FormEmpty>Nothing is ruled out.</FormEmpty>
                    ) : (
                        ruleOutRows.map((fact) => (
                            <RuleOutRow
                                key={fact.field}
                                fact={fact}
                                values={
                                    ruleOutFor(draft, fact.field)?.values ?? []
                                }
                                vocabulary={prospect.vocabulary}
                                hasLens
                                disabled={saving}
                                lockedReason={null}
                                onChange={(values) =>
                                    setEdited(
                                        withRuleOut(draft, fact.field, values),
                                    )
                                }
                                onRemove={() => {
                                    setOpenRuleOuts(
                                        openRuleOuts.filter(
                                            (field) => field !== fact.field,
                                        ),
                                    );
                                    setEdited(
                                        withRuleOut(draft, fact.field, []),
                                    );
                                }}
                            />
                        ))
                    )}
                </FormSection>

                {questions.length > 0 && (
                    <FormSection
                        title="Questions we ask"
                        icon={ChatBubbleLeftRightIcon}
                        description="Answered per company. You can change these once you have an account."
                    >
                        {questions.map((question) => (
                            <FormRow key={question.id} label={question.text}>
                                <Badge
                                    color={
                                        question.answer === "yes"
                                            ? "green"
                                            : "zinc"
                                    }
                                >
                                    Ideal answer: {question.answer}
                                </Badge>
                            </FormRow>
                        ))}
                    </FormSection>
                )}
            </div>

            <div className="flex flex-col gap-3">
                <AuthError
                    message={
                        error ??
                        (isEmpty
                            ? "A profile needs at least one fact."
                            : undefined)
                    }
                />
                <div className="flex flex-wrap items-center gap-4">
                    <BrandButton
                        loading={saving}
                        disabled={isEmpty}
                        onClick={() => void save()}
                    >
                        Save and continue
                    </BrandButton>
                    <Link
                        to={auditPath(token)}
                        className="text-sm text-brand-grey-warm underline"
                    >
                        Back without saving
                    </Link>
                </div>
            </div>
        </Page>
    );
}

/** The editor is wider than the auth card, so it has a page of its own. */
function Page({ children }: { children: React.ReactNode }) {
    useLightSurface();

    return (
        <div className="min-h-dvh bg-white font-sans text-brand-purple">
            <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10">
                <Logo
                    variant="color+primary"
                    className="h-6 w-auto self-start"
                />
                {children}
            </div>
        </div>
    );
}
